import { ref, onMounted, onUnmounted, type Ref } from "vue";
import { Facade } from "@/drawing";
import { Logger } from "@/logger";
import type { ActivityEntity, ActivityKind } from "@/drawing/parsing/activity";
import { EntityType } from "@/drawing/entities/detection";
import { activityEventRepository } from "@/repositories/activityEvent.repository";
import type { DRActivityEvent } from "@/types/activity-event.types";

export type { ActivityEntity };

export type ActivityEventKind = "created" | "modified" | "deleted";

const TYPE_TO_KIND: Record<DRActivityEvent["type"], ActivityEventKind> = {
  CREATED: "created",
  MODIFIED: "modified",
  DELETED: "deleted",
};

/**
 * Ligne du fil : un event = une ligne (log quasi-brut). `handle` présent seulement
 * si l'entité est encore vivante (repérable sur le plan), quel que soit `eventKind`.
 */
export interface ActivityRow
  extends Omit<ActivityEntity, "handle" | "createdAt" | "background"> {
  handle?: string;
  eventKind: ActivityEventKind;
  /** Epoch ms de l'event (ou du `createdAt` DR_META pour une ligne "created" synthétique). */
  time: number;
  /** > 1 si plusieurs MODIFIED consécutifs (même entité, rien d'autre entre eux) ont été fusionnés. */
  mergedCount?: number;
  /** Contexte du changement (ligne "modified" uniquement) — cf. `OplogSnapshot`. */
  changeKind?: string;
  oldValue?: string;
  newValue?: string;
}

type DisplayData = Pick<
  ActivityRow,
  | "kind"
  | "entityType"
  | "text"
  | "defectName"
  | "color"
  | "layer"
  | "handle"
  | "markerLabel"
>;

/**
 * Données d'affichage d'une entité, quel que soit l'event rendu : si elle est encore
 * vivante, on lit le DWG (à jour) ; sinon la seule source restante est le snapshot du
 * dernier event DELETED (l'entité a disparu, rien d'autre ne peut la décrire).
 */
function displayData(
  entityUid: string,
  liveByUid: Map<string, ActivityEntity>,
  events: DRActivityEvent[]
): DisplayData {
  const live = liveByUid.get(entityUid);
  if (live) {
    return {
      kind: live.kind,
      entityType: live.entityType,
      text: live.text,
      defectName: live.defectName,
      color: live.color,
      layer: live.layer,
      markerLabel: live.markerLabel,
      handle: live.handle,
    };
  }
  const lastDeleted = [...events].reverse().find((e) => e.type === "DELETED");
  return {
    kind: (lastDeleted?.snapshot?.kind as ActivityKind) ?? "geometry",
    entityType:
      (lastDeleted?.snapshot?.entityType as EntityType) ?? EntityType.Unknown,
    text: lastDeleted?.snapshot?.text,
    defectName: lastDeleted?.snapshot?.defectName,
    color: undefined,
    layer: "",
    markerLabel: undefined,
    handle: undefined,
  };
}

const MODIFIED_CLUSTER_TIME_THRESHOLD_MS = 2000;

/**
 * Construit le fil : quasi un event = une ligne. Seule exception, les `MODIFIED`
 * rapprochés en temps sur une même entité (rien d'autre de cette entité entre les deux)
 * sont fusionnés en une seule ligne (`mergedCount`) — sinon une simple retouche en
 * plusieurs micro-étapes (drag de sommet, etc.) noierait le fil.
 *
 * Une entité vivante SANS aucun event oplog (dessinée avant la capture, ou fond de
 * plan jamais touché) obtient une ligne "created" synthétique depuis son `createdAt`
 * DR_META — SAUF si elle est `background` (identité stampée à la volée, jamais une
 * vraie création DR) : dans ce cas elle ne doit apparaître QUE si un event actif
 * existe, sinon un fond de plan supprimé-puis-annulé (undo) réapparaîtrait comme un
 * faux "créé" pour toujours (le DR_META reste, l'event a été retiré par l'undo).
 */
function buildRows(live: ActivityEntity[], events: DRActivityEvent[]): ActivityRow[] {
  const liveByUid = new Map(live.map((e) => [e.entityUid, e]));

  const byUid = new Map<string, DRActivityEvent[]>();
  for (const e of events) {
    const arr = byUid.get(e.entityUid);
    if (arr) arr.push(e);
    else byUid.set(e.entityUid, [e]);
  }
  for (const arr of byUid.values()) arr.sort((a, b) => a.clientTs - b.clientTs);

  const rows: ActivityRow[] = [];

  for (const [entityUid, evs] of byUid) {
    const data = displayData(entityUid, liveByUid, evs);
    let i = 0;
    while (i < evs.length) {
      if (evs[i].type === "MODIFIED") {
        let j = i + 1;
        while (
          j < evs.length &&
          evs[j].type === "MODIFIED" &&
          evs[j].clientTs - evs[j - 1].clientTs <= MODIFIED_CLUSTER_TIME_THRESHOLD_MS
        )
          j++;
        // Contexte affiché = le dernier event de la rafale (cohérent avec `time`).
        const last = evs[j - 1];
        rows.push({
          ...data,
          entityUid,
          eventKind: "modified",
          time: last.clientTs,
          mergedCount: j - i,
          changeKind: last.snapshot?.changeKind,
          oldValue: last.snapshot?.oldValue,
          newValue: last.snapshot?.newValue,
        });
        i = j;
      } else {
        rows.push({
          ...data,
          entityUid,
          eventKind: TYPE_TO_KIND[evs[i].type],
          time: evs[i].clientTs,
        });
        i++;
      }
    }
  }

  for (const e of live) {
    if (byUid.has(e.entityUid) || e.background) continue;
    rows.push({ ...e, eventKind: "created", time: e.createdAt ?? 0 });
  }

  // Plus récent en premier.
  rows.sort((a, b) => b.time - a.time);
  return rows;
}

/**
 * Fil « Activité » — VIEWER + oplog local (lecture seule, dispo offline).
 *
 * Log quasi-brut des events DR (DWG en mémoire, légende-indépendant, fusionné avec
 * l'oplog local déjà scopé à la lignée courante). Le regroupement en observations
 * (mutation, online-only) viendra dans un palier ultérieur.
 */
export function useActivityFeed(drawingId: number): {
  elements: Ref<ActivityRow[]>;
  loading: Ref<boolean>;
  locate: (handle: string) => void;
  refresh: () => void;
} {
  const elements = ref<ActivityRow[]>([]);
  const loading = ref(true);

  let historyHandler: (() => void) | null = null;
  let reloadTimer: ReturnType<typeof setTimeout> | null = null;

  async function loadElements(): Promise<void> {
    loading.value = true;
    const live = Facade.activity.listEntities();
    const events = await activityEventRepository.getByDrawing(drawingId);
    elements.value = buildRows(live, events);
    loading.value = false;
  }

  /** Repère un élément sur le plan (zoom) sans le sélectionner. */
  function locate(handle: string): void {
    Logger.info(`Activity : clic on element ${handle}`);
    Facade.selection.zoomToHandles([handle]);
  }

  function refresh(): void {
    void loadElements();
  }

  /**
   * A1 — Réactivité : reflète en direct dessins / éditions / suppressions / undo
   * faits pendant que le panneau est ouvert. Débouncé pour ne pas rescanner le
   * DWG à chaque commande d'une rafale.
   */
  function scheduleReload(): void {
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => {
      reloadTimer = null;
      void loadElements();
    }, 250);
  }

  onMounted(() => {
    Logger.info("Activity : open tab");
    // Facade pas prête (ex. instance périmée par le HMR) : on n'accède PAS aux
    // getters de manager (ils lèvent) — le panneau s'ouvre vide plutôt que planter.
    if (!Facade.isInitialized) {
      loading.value = false;
      return;
    }
    historyHandler = (): void => scheduleReload();
    Facade.history.onChange(historyHandler);
    refresh();
  });

  onUnmounted(() => {
    // Facade a pu être désinitialisée entre-temps (fermeture du dessin, HMR) — même
    // garde qu'au mount, sinon `Facade.history` lève "Facade not initialized".
    if (historyHandler && Facade.isInitialized) {
      Facade.history.offChange(historyHandler);
    }
    if (reloadTimer) clearTimeout(reloadTimer);
  });

  return { elements, loading, locate, refresh };
}
